import React, { useState, useContext, useEffect } from "react";
import Head from "../Components/Layout/Head";
import FooterNew from "../Components/Layout/FooterNew";
import { CheckCircle, XCircle } from "lucide-react";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { isValidPhoneNumber } from "react-phone-number-input";
import { CountryContext } from "../CountryContext";
import ApiPath from "../Components/Common/Apiurl";

const WebinarRegistrationPage = () => {

    const country = useContext(CountryContext);

    const [selectedCountry, setSelectedCountry] = useState("India");
    const [phone, setPhone] = useState("");
    const [loading, setLoading] = useState(false);
    const [formVisible, setFormVisible] = useState(true);
    const [formError, setFormError] = useState("");

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        firmName: "",
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        setSelectedCountry(country === "United States" ? "United States" : "India");
    }, [country]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setErrors({ ...errors, [e.target.name]: "" });
    };

    const validate = () => {
        let err = {};
        let valid = true;


        if (!formData.name) {
            err.name = "Name is required";
            valid = false;
        }

        if (!formData.email.match(/^\S+@\S+\.\S+$/)) {
            err.email = "Valid email required";
            valid = false;
        }

        if (!phone || !isValidPhoneNumber(phone)) {
            err.phone = "Valid phone required";
            valid = false;
        }

        setErrors(err);
        return valid;


    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;


        setLoading(true);

        try {
            const res = await fetch(ApiPath + "/api/webinarController/registerWebinar", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    ...formData,
                    phone,
                    country: selectedCountry,
                }),
            });

            const data = await res.json();
            console.log("Response from backend:", data);

            //  Handle backend success flag
            if (data.status) {
                setFormVisible(false);
                setFormData({ name: "", email: "", firmName: "" });
                setPhone("");
                setFormError(""); // clear error
            } else {
                setFormError(data.message || "Registration failed");
            }

        } catch (err) {
            console.error(err);
            setFormError("Server error. Please try again.");
        }

        setLoading(false);
    };

    const getCountryCode = (country) => {
        const map = {
            India: "IN",
            "United States": "US",
            Australia: "AU",
            Germany: "DE",
            Japan: "JP",
            Singapore: "SG",
        };
        return map[country] || "US";
    };

    useEffect(() => {
        if (!phone) return;

        const countryCode = getCountryCode(selectedCountry);

        // safer check
        if (phone && !phone.startsWith("+")) return;

        if (phone && !phone.includes("+" + getDialCode(countryCode))) {
            setPhone("");
        }

    }, [selectedCountry]);

    const getDialCode = (countryCode) => {
        const dialMap = {
            IN: "91",
            US: "1",
            AU: "61",
            DE: "49",
            JP: "81",
            SG: "65",
        };
        return dialMap[countryCode] || "";
    };
    return (
        <>
            <Head />


            <div className="bg-gradient-to-br from-blue-50 via-white to-blue-100 py-12 px-0 md:px-6 lg:px-8">

                <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12 items-center">

                    {/* LEFT CONTENT */}
                    <div className="space-y-6 text-center lg:text-left px-3 ">

                        <span className="inline-block bg-green-100 text-green-700 px-4 py-1 rounded-full text-sm font-semibold shadow-sm">
                            Limited Seats Available
                        </span>

                        <h1 className="text-3xl md:text-5xl font-bold leading-tight text-gray-900">
                            Free AI Automation Webinar
                            <span className="block text-blue-600 mt-2">
                                Save Hours Daily
                            </span>
                        </h1>

                        <p className="text-gray-600 text-lg">
                            Discover how businesses automate repetitive work, reduce costs, and scale faster using AI.
                        </p>

                        {/* Highlights */}
                        <div className="space-y-3 text-gray-800 text-base">
                            <p>✅ Live AI Automation Demo</p>
                            <p>✅ Real Case Studies</p>
                            <p>✅ Interactive Q&A Session</p>
                        </div>

                        {/* Trust Box */}
                        <div className="bg-white p-5 rounded-xl shadow-md border border-gray-100">
                            <p className="text-sm text-gray-700">
                                📩 Webinar details will be sent instantly after registration
                            </p>
                            <p className="text-sm text-gray-500 mt-2">
                                🔒 100% privacy guaranteed. No spam.
                            </p>
                        </div>

                    </div>

                    {/* FORM SECTION */}
                    <div className="relative flex justify-center lg:justify-end">

                        {/* Glow Background */}
                        <div className="absolute inset-0 bg-blue-200 opacity-20 blur-2xl rounded-2xl"></div>

                        <div className="relative w-full max-w-md bg-white p-6 sm:p-8 md:rounded-2xl shadow-2xl border border-gray-100">

                            {!formVisible && (
                                <div className="text-center py-8 animate-fadeIn">

                                    <div className="bg-green-100 w-20 h-20 flex items-center justify-center rounded-full mx-auto mb-4 shadow">
                                        <CheckCircle size={40} className="text-green-600" />
                                    </div>

                                    <h2 className="text-2xl font-bold text-gray-900">
                                        You're Registered
                                    </h2>

                                    <p className="text-gray-600 mt-2">
                                        Webinar details have been sent to your email.
                                    </p>

                                    <p className="text-sm text-gray-400 mt-3">
                                        Check your inbox (and spam folder).
                                    </p>
                                </div>
                            )}

                            {formError && (
                                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-start gap-3 animate-fadeIn">
                                    <XCircle size={22} className="text-red-500 mt-0.5" />
                                    <div>
                                        <p className="text-sm font-medium">Registration Failed</p>
                                        <p className="text-sm">{formError}</p>
                                    </div>
                                </div>
                            )}
                            {formVisible && (
                                <>
                                    <h2 className="text-xl font-semibold mb-6 text-center">
                                        Reserve Your Seat
                                    </h2>

                                    <form onSubmit={handleSubmit} className="space-y-5">

                                        {/* Name */}
                                        <div>
                                            <input
                                                name="name"
                                                placeholder="Full Name *"
                                                value={formData.name}
                                                onChange={handleChange}
                                                className="w-full border px-4 py-3 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition"
                                            />
                                            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                                        </div>

                                        {/* Email */}
                                        <div>
                                            <input
                                                name="email"
                                                placeholder="Email Address *"
                                                value={formData.email}
                                                onChange={handleChange}
                                                className="w-full border px-4 py-3 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition"
                                            />
                                            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                                        </div>

                                        {/* Phone */}
                                        <div className="border rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-blue-500 transition">
                                            <PhoneInput
                                                key={selectedCountry}
                                                countries={["IN", "US"]}
                                                defaultCountry={getCountryCode(selectedCountry)}
                                                international
                                                value={phone}
                                                onChange={setPhone}
                                                placeholder="Enter phone number *"
                                            />
                                        </div>
                                        {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}

                                        {/* Firm (Optional) */}
                                        <input
                                            name="firmName"
                                            placeholder="Firm Name (Optional)"
                                            value={formData.firmName}
                                            onChange={handleChange}
                                            className="w-full border px-4 py-3 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition"
                                        />

                                        {/* Country */}
                                        <select
                                            value={selectedCountry}
                                            onChange={(e) => setSelectedCountry(e.target.value)}
                                            className="w-full border px-4 py-3 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                        >
                                            <option>India</option>
                                            <option>United States</option>
                                        </select>

                                        {/* CTA */}
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className={`relative group w-full py-3 rounded-lg font-semibold text-white 
                                                transition-all duration-300 shadow-md overflow-hidden
                                                ${loading
                                                    ? "bg-gray-400 cursor-not-allowed"
                                                    : "bg-blue-600 hover:bg-blue-700 hover:shadow-lg"
                                                }`}
                                        >

                                            {/* Glow effect */}
                                            <span className="absolute inset-0 bg-gradient-to-r from-blue-500 to-indigo-600 opacity-0 group-hover:opacity-20 transition duration-300"></span>

                                            <span className="relative flex items-center justify-center gap-2">

                                                {loading ? (
                                                    <>
                                                        <span className="animate-spin border-2 border-white border-t-transparent rounded-full w-4 h-4"></span>
                                                        Registering...
                                                    </>
                                                ) : (
                                                    <>
                                                        Reserve My Seat
                                                        <span className="transform transition group-hover:translate-x-1">→</span>
                                                    </>
                                                )}

                                            </span>

                                        </button>

                                    </form>

                                    <p className="text-xs text-gray-400 text-center mt-4">
                                        No spam. Only webinar details.
                                    </p>
                                </>
                            )}

                        </div>
                    </div>
                </div>
            </div>

            <FooterNew />
        </>


    );
};

export default WebinarRegistrationPage;
